import { MortgageCalculator } from "@component/Mortgage/Calculator/MortgageCalculator";
import { GreenLoanSmallCalculator } from "@component/Mortgage/GreenLoanSmallCalculator/GreenLoanSmallCalculator";
import type { FAQSchema } from "@component/StructuredDataSchema";
import { StructuredDataSchema } from "@component/StructuredDataSchema";
import { YoutubeVideo } from "@component/Widget/YoutubeVideo/YoutubeVideo";
import {
	faCircleInfo as fasCircleInfo,
	faCalculator as fasCalculator,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import { Button, InfoPopup, List, scrollToElement } from "bm-ui-kit";
import type { ReactNode } from "react";
import React from "react";
import { Col, Row, Table } from "react-bootstrap";
import { LandingBottom } from "../../components/LandingBottom";
import { LandingContainer } from "../../components/LandingContainer";
import { LandingGyikBig } from "../../components/LandingGyikBig";

/**
 * @Routing
 *
 * @title "NHP Zöld Otthon Program | támogatott lakáshitelek"
 * @description "A hitelösszeg maximum 70 millió forint lehet, a kamat végig fix 2,5%, a futamidő legfeljebb 25 év és akár 0% kamatozású CSOK hitel is igényelhető mellé."
 * @route "/zold-hitel/"
 */

const headingClass = "h1 mb-lg-4 mb-md-3 mb-2 text-center";

export default class Landing extends React.PureComponent {
	public render(): ReactNode {
		return (
			<>
				<div id="calculator">
					<MortgageCalculator
						title="NHP Zöld Otthon Program (Zöld hitel kalkulátor)"
						description="Spórolj százezreket! Személyre szabott megoldást adunk hiteligényedre."
						variant="GREEN_LOAN"
					/>
				</div>
				<LandingContainer size="lg">
					<Row className="align-items-center">
						<Col md={6} className="pr-5">
							<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">A zöld hitel legfontosabb jellemzői</h2>
							<List itemStyle="tick" variant="success" className="mt-3 mb-4">
								<List.Item>
									A hitelösszeg maximum <strong>70 millió</strong> forint lehet
								</List.Item>
								<List.Item>
									A kamat <strong>fixen 2,5%</strong> futamidő végéig
								</List.Item>
								<List.Item>
									A futamidő legfeljebb <strong>25 év</strong> lehet
								</List.Item>
								<List.Item>
									<strong>0% kamatozású Zöld CSOK-hitel</strong> is igényelhető mellé
								</List.Item>
								<List.Item>
									<strong>Alacsony energiaigényű</strong> ingatlan vásárlására, vagy építésére
								</List.Item>
								<List.Item>
									A kölcsön <strong>október 4-től</strong> igényelhető
								</List.Item>
								<List.Item>
									Kizárólag <strong>magánszemélyek</strong> számára érhető el
								</List.Item>
							</List>
						</Col>
						<Col md={6}>
							<YoutubeVideo
								title="Mit érdemes tudni az új zöld hitelről?"
								videoId="Uiz7e0uT8zE"
								description="Október 4-től elérhető az új zöld hitel. A fix 2,5 százalékos kamatozású kölcsön páratlan lehetőség az új lakást vásárlóknak, építkezőknek. Sőt a csok támogatást igénylők akár kamatmentesen is megkaphatják a kölcsönt. Jöjjenek a részletek!"
								uploadDate="2021-10-12"
							/>
						</Col>
					</Row>
				</LandingContainer>

				<LandingContainer>
					<Row className="justify-content-center">
						<Col md={9}>
							{this.renderTableOfContents()}

							<hr />
							<h2 id="green-loan-goal" className={headingClass}>
								A zöld hitel célja
							</h2>
							<p>
								Az Magyar Nemzeti Bank (MNB) a NHP Zöld Otthon Program (NHP ZOP) elindításával szeretné
								elérni, hogy a{" "}
								<strong>hazai ingatlanállomány minél korszerűbb és környezetkímélőbb</strong> legyen.
								Ennek részeként elindult október 4-én a jegybank új lakáshitel konstrukciója, amelynek
								kihasználásával az alacsony energiafelhasználású lakást vásárlók, építtetők kedvező
								feltételek mellett kaphatnak kölcsönt.
							</p>
							<hr />
							<h2 id="green-loan-conditions" className={headingClass}>
								A zöld hitel igénylés feltételei
							</h2>
							<p>
								Mivel banki hitelről van szó, ezért teljesítened kell a bankok által a hiteligénylőkkel
								szemben támasztott alapvető elvárásokat: szükséges a kölcsön felvételéhez megfelelő
								munkaviszony, jövedelem… Jelen ismereteink szerint az alapfeltételek mellett csupán
								néhány fontos speciális elvárást szükséges teljesítened a jegybanki hitel
								igénybevételéhez.
							</p>
							<h3>Ingatlannal kapcsolatos elvárások</h3>
							<ul>
								<li>
									<strong>Új lakás, ház vásárlásához, építéséhez</strong> igényelhető a támogatott
									hitel.
								</li>
								<li>
									Kizárólag <strong>alacsony energiaigényű házakat</strong> lehet megfinanszírozni a
									kölcsönből. (Az energetikai tanúsítvány minősítése alapján el kell érnie az
									ingatlannak legalább a „BB” kategóriát. Emellett az épület primer
									energiafelhasználása nem haladhatja meg a 90 kWh/m2/év értéket.)
								</li>
								<li>
									A kölcsönt kizárólag a{" "}
									<strong>végleges adásvételi szerződés megkötését követően</strong> lehet
									megigényelni. Előszerződés a hitel felvételéhez nem elégséges.
								</li>
							</ul>
							<h3>Az igénylővel kapcsolatos elvárások</h3>
							<ul>
								<li>
									Legfeljebb 4 adósa lehet a kölcsönnek. (Az igénylő mellett a pénzintézetek
									jellemzően társigénylőként vonják be a hitelügyletbe az igénylő házas/élettársát és
									a tulajdonszerzőket is.)
								</li>
								<li>
									Kizárólag a hitelügylet adósai szerezhetnek tulajdonjogot az ingatlanban. (Több adós
									esetén az arányokra nincs elvárás: elfogadható az is, ha csak az egyik adós szerez
									tulajdonjogot az ingatlanban.)
								</li>
								<li>
									A tulajdonszerző adósoknak legalább 10 évig életvitelszerűen bent kell élniük az
									adott ingatlanban.
								</li>
								<li>Egy személy kizárólag egy zöld hitelnek lehet az adósa.</li>
							</ul>
							<p>
								Az elvárások mellett külön ki kell emelni azt, hogy mi az, amit várhatóan nem szükséges
								teljesítened a kölcsön felvételéhez:{" "}
								<strong>nincsen gyermekekre, családi állapotra vonatkozó elvárás.</strong> Vagyis
								egyedülállóként, gyermek nélkül is ki tudod használni a konstrukció előnyeit.
							</p>
							<p>
								Természetesen a zöld hitelprogram még gyerekcipőben jár, a feltételek még változhatnak.{" "}
								<strong>
									A feltételeket, elvárásokat folyamatosan frissítjük a megjelent újabb adatok, hírek
									alapján!!!
								</strong>
							</p>
							<p>
								Az aktuálisan elérhető lakáshitel ajánlatokat megtalálhatod a{" "}
								<a href="/lakashitel-kalkulator/?link_type=landing-link">
									Bankmonitor lakáshitel kalkulátorában.
								</a>
							</p>
							<hr />
							<h2 id="available-loan-bank" className={headingClass}>
								Hol érhető el jelenleg a zöld hitel?
							</h2>
							<p>
								Az NHP Zöld Otthon Programja október 4-én elindult. Számos pénzintézetnél elérhető már a
								kedvezményes hitelkonstrukció:
							</p>
							<ul>
								<li>Budapest Bank</li>
								<li>CIB Bank</li>
								<li>ERSTE Bank</li>
								<li>Gránit Bank</li>
								<li>K&H Bank</li>
								<li>MBH Bank</li>
								<li>OTP Bank</li>
								<li>Raiffeisen Bank</li>
								<li>UniCredit Bank</li>
							</ul>
							<p>
								Az MNB oldala alapján az alábbi pénzintézetek kötöttek már szerződést a kölcsön
								forgalmazására: a Budapest Bank, a CIB Bank, a DT Bank, a Fundamenta Zrt, az ERSTE Bank,
								a Gránit Bank, a K&H Bank, a MagNet Bank, a MBH Bank, az Oberbank, az OTP Bank, a
								Polgári Bank, a Raiffeisen Bank, a Sopron Bank Burgenland és az UniCredit Bank biztos
								forgalmazni fogja a konstrukciót.
							</p>
							<hr />
							<h2 id="available-loan-goals" className={headingClass}>
								Milyen célra fordítható a hitelösszeg?
							</h2>
							<p>
								Alapvetően új lakás vásárlására és építésre használható fel a kedvezményes hitel.
								Emellett elfogadható cél lesz a jövőben meglévő zöld hitel kiváltása is. (Meglévő
								korábban folyósított sima lakáshitel előtörlesztésére, kiváltására azonban nem
								fordítható az adott összeg.)
							</p>
							<p>
								Új lakásnak minősül minden cég által épített, építtetett lakás, melyet az eladó első
								ízben értékesítés magánszemély részére. (Ez azt is jelenti, hogy magánszemély által
								épített ház megvásárlására nem fordítható az MNB Zöld lakáshitele akkor sem, ha abban az
								ingatlanban még nem lakott senki.)
							</p>
							<h3>Az alábbi tevékenységek felelhetnek meg lakásépítésnek:</h3>
							<ul>
								<li>Meglévő telken új lakás, ház építése.</li>
								<li>
									Telek vásárlása, és azon új lakás, ház építése. Jelen esetben elvárás, hogy a
									hitelösszegnek legfeljebb a felét lehet a telekvásárlásra fordítani. Emellett az
									építkezésnek a telekvásárlását követő egy éven belül meg kell kezdődnie.
								</li>
								<li>Félkész lakás megvásárlására és befejezésére is fordítható a zöld hitel.</li>
							</ul>
							<p>
								Fontos kihangsúlyozni, hogy az egyes bankok nem nyújtanak az összes célra lakáshitelt az
								MNB zöld otthon program keretében.
							</p>
							<hr />
							<h2 id="much-more-time" className={headingClass}>
								Sokkal több idő áll rendelkezésre a ház átadására, az építkezés befejezésére
							</h2>
							<p>
								A zöld hitelnél a rendelkezésre tartási idő 4 év is lehet. Ez a szerződéskötést követő
								időszak, amelyen belül folyósítási feltételeket teljesítened kell.
							</p>
							<p>
								Egy lakás vásárlása esetén elvárás mindenképpen, hogy a ház elkészüljön, rendelkezzen
								használatbavételi engedéllyel. Építkezés esetén pedig el kell érni az adott szakasz,
								részlet lehívásához szükséges készültségi fokot.
							</p>
							<p>
								Ez egy sima vásárlási hitel esetében 3-18 hónap szokott lenni. Ehhez képest a 4 éves
								időszak – az első részlet lehívására 3 év áll rendelkezésre – extrém hosszúnak
								tekinthető. Fontos tudni, hogy egyes bankok ennél szigorúbb feltételeket, elvárásokat
								alkalmaznak a kölcsön folyósításának határidejével kapcsolatban.
							</p>
							<hr />
							<h2 id="why-is-it-worth-it" className={headingClass}>
								Miért érné meg kihasználnod az új hitelkonstrukciót?
							</h2>
							<p>
								Fontos kihangsúlyozni, hogy jelenleg fix kamatozású hitelt ilyen kedvező feltételekkel
								nem tudnál igényelni. Még a{" "}
								<a href="/csok-feltetelei/?link_type=landing-link" target="_blank">
									CSOK támogatás
								</a>{" "}
								mellé kérhető{" "}
								<strong>
									<a href="/csok/?link_type=landing-link" target="_blank">
										CSOK-hitel
									</a>{" "}
									is 3 százalékos kamat
								</strong>{" "}
								mellett érhető el.
							</p>
							<p>
								Az elérhető maximális hitelösszeg – 70 millió forint – is kiemelkedően magasnak
								tekinthető. Ez a hitelösszeg már elégséges lehet egy újépítésű lakás megvásárlásához
								szükséges teljes hitelösszeg fedezésére. (A jelenleg elérhető támogatott CSOK-hitelből
								legfeljebb 15 millió forintot tudnál felvenni.)
							</p>
						</Col>
					</Row>
				</LandingContainer>
				{this.renderNotificationBlock()}
				<LandingContainer>
					<Row className="justify-content-center">
						<Col md={9}>
							<h3>A zöld hitel olcsóbb, mint a támogatott CSOK-hitel</h3>
							<p>
								Három gyermekkel 15 millió forint, 3 százalékos kamatozású támogatott hitelt vehetnél
								fel a CSOK (családi otthonteremtési kedvezmény) mellé. Erre az összegre 20 éves
								futamidőt választva <strong>83 ezer forintot kellene fizetned</strong> havonta.
							</p>
							<p>
								Ha megvárnád az októberben induló zöld hitelt, akkor 2,5 százalékos kamat mellett már
								megkaphatnád a 15 millió forintot. A havi törlesztőrészleted pedig csupán{" "}
								<strong>79 ezer forint lenne</strong>. Vagyis{" "}
								<strong>havi szinten 3 700 forintot spórolhatnál</strong>, ami 20 év alatt{" "}
								<strong>889 ezer forint megtakarítást</strong> eredményezne.
							</p>
							{this.renderTable1()}
							<GreenLoanSmallCalculator outsideTitleLink={null} colorScheme="light" />
							<h3>Mennyit nyerhet egy gyermektelen egyedülálló a zöld hitellel?</h3>
							<p>
								Gyermektelen egyedülállóként jelenleg{" "}
								<a href="/lakashitel-kalkulator/?link_type=landing-link">
									piaci lakáshitelt igényelhetnél a lakás megvásárlásához
								</a>
								. 30 millió forint lakáshitelt jelenleg 4,52%-os fix kamat mellett tudnál felvenni.
								(2021.09.06-án érvényes kondíciók alapján.) 20 éves futamidőt választva a{" "}
								<strong>havi törlesztőrészleted 190 ezer forint lenne.</strong>
							</p>
							<p>
								Amennyiben októberben felveszed az MNB zöld hitelét, akkor ezzel fix 2,5 százalékos
								kamaton megkaphatnád a szükséges 30 millió forintot. A törlesztőrészleted pedig 20 éven
								keresztül <strong>havi 159 ezer forintra csökkenne</strong>. Vagyis{" "}
								<strong>havonta 31 ezer forintot spórolhatnál</strong>, ami 20 év alatt{" "}
								<strong>7,4 millió forint megtakarítást</strong> eredményezne.
							</p>
							{this.renderTable2()}
							<hr />
							<h2 id="interest-free-csok" className={headingClass}>
								Kamatmentes lesz a CSOK-hitel, ha zöld hitelként igényelnéd
							</h2>
							<p>
								Kamatmentessé, azaz teljesen ingyenessé válhat a CSOK-hitel számodra, amennyiben a Zöld
								Otthon Program keretében igényelnéd azt. Ez önmagában a támogatott hitel
								törlesztőrészletére is komoly hatással lehet: jelenleg 15 millió forint hitel havi
								törlesztőrészlete - 3 százalékos kamat és 20 éves futamidő mellett - 83 ezer forint
								lenne. Ugyanezen hitel kamatmentes törlesztőrészlete 62 500 forint lenne, vagyis{" "}
								<strong>20-21 ezer forintot spórolhatnál</strong> azzal, hogy a CSOK támogatást és az
								MNB zöld otthon programját együttesen használod ki. Ez 20 év alatt közel{" "}
								<strong>5 millió forint megtakarítást</strong> jelentene.
							</p>
							{this.renderTable3()}
							<hr />
							<h2 id="plus-loan" className={headingClass}>
								Nem elég a Zöld CSOK-hitel? Sima zöld hitelt is felvehetsz mellé!
							</h2>
							<p>
								A kamatmentes zöld CSOK-hitel egy remek ajánlat, viszont sok esetben nem lenne elég
								számodra lakáscélod megvalósításához a maximálisan elérhető 10/15 millió forint
								hitelösszeg.
							</p>
							<p>
								Jó hír azonban, hogy a kamatmentes zöld CSOK-hitel mellé felveheted a sima 2,5
								százalékos kamatozású zöld hitelt is abban az esetben, ha nem lenne elég lakáscélod
								megvalósításához az ingyenes kölcsön. (Jelen esetben a két hitel együttes összege nem
								haladhatja meg a 70 millió forintot.)
							</p>
							<p>
								Ezzel tovább nőhet a Zöld Otthon Program pénzügyi előnye. Ha például a 15 millió Ft
								ingyenes hitel mellép szükséged lenne még 30 millió Ft kölcsönre, akkor az
								össztörlesztőrészleted havi 221 500 forint lenne 20 éves futamidő esetén. Ha ezen
								összeget az MNB Zöld Otthon Programján keretein kívül vennéd fel, akkor havi
								fizetnivalód 273 ezer forint lenne havonta.
							</p>
							<h3>
								Havi szinten közel 52 ezer forintot spórolhatsz, ami 20 év alatt 12,4 millió forint
								megtakarítást eredményezne.
							</h3>
							{this.renderTableCsokPlus()}
						</Col>
					</Row>
				</LandingContainer>
				{this.renderNotificationBlock()}
				<LandingContainer className="bottom-info">
					<Row>
						<Col md={4} className="text-center mb-4 mb-md-0">
							<div className="h2 text-secondary">Akár 70M Ft</div>
							igényelhető hitelösszeg
						</Col>
						<Col md={4} className="text-center mb-4 mb-md-0">
							<div className="h2 text-secondary">2,5%</div>
							fix hitelkamat
						</Col>
						<Col md={4} className="text-center">
							<div className="h2 text-secondary">akár 25 év</div>
							maximális futamidő
						</Col>
					</Row>
				</LandingContainer>
				<LandingContainer>
					<Row className="justify-content-center">
						<Col md={9}>
							<LandingGyikBig
								id="gyik"
								title="Gyakori kérdések"
								questions={{
									"Emelkedhetnek a lakásárak októbertől az új támogatott hitel bevezetése miatt?": (
										<p>
											Az új támogatások természetesen befolyásolhatják a lakásárakat is.
											Ugyanakkor a lakáspiacra jelenleg számos tényező hat ezek együttes hatását
											pedig előre nehéz megbecsülni.
										</p>
									),
									"Elfogyhat a 200 milliárd forintos keretösszeg?": (
										<p>
											Nem tudhatjuk előre, hogy meddig lesz elég az MNB által biztosított
											keretösszeg. (2 857 igénylő tudná felvenni a kölcsönt, ha mindenki
											kihasználná a maximális 70 millió Ft hitelösszeget.)
										</p>
									),
									"Mennyire szigorú a „BB” energetikai besorolás?": (
										<p>
											A kategória elnyeréséhez elvárás, hogy a ház energiafelhasználásának{" "}
											<strong>legalább 25 százaléka megújuló forrásból származzon.</strong> (2022.
											júliusától már csak ennek megfelelő lakás készülhet el.)
										</p>
									),
									"Szükséges gyermek a zöld hitelhez?": <p>Nem szükséges</p>,
									"Szükséges gyermek a kamatmentes Zöld CSOK-hitelhez?": (
										<p>
											Igen, legalább két meglévő, vagy vállalt gyermek szükséges a kamatmentesség
											elnyeréséhez.
										</p>
									),
									"Csak házasok kaphatják meg a zöld hitelt?": (
										<p>Nem, egyedülállóként is fel lehet venni a zöld hitelt.</p>
									),
									"Csak házasok kaphatják meg a kamatmentes Zöld CSOK-hitelt?": (
										<p>
											Nem egyedülállók is megkaphatják az ingyenes zöld hitelt. Egyedülállóként
											viszont elvárás, hogy legyen legalább két meglévő gyermek.
										</p>
									),
									"Fel lehet venni a Babaváró hitelt a zöld hitellel együtt?": (
										<p>Igen, erre vonatkozóan nincsen tiltás.</p>
									),
									"Fel lehet venni a CSOK támogatást a zöld hitel mellé?": (
										<p>
											Igen, sőt a kamatmentes zöld CSOK-hitelnek igénylési feltétele is, hogy a
											CSOK támogatást legalább két gyermekre igénybe vegyék.
										</p>
									),
									"Fel lehet venni a falus CSOK-ot a zöld hitel mellé?": (
										<p>Nem, mert eltérő célokra használható fel a két támogatási forma.</p>
									),
									"Fel lehet venni a felújítási támogatást a zöld hitel mellé?": (
										<p>
											Igen, újépítésű lakás „felújítására” is fordítható az otthonfelújítási
											támogatás.
										</p>
									),
									"Fel lehet venni a zöld hitelt meglévő lakás felújítására, korszerűsítésére?": (
										<p>
											Nem, a zöld hitel kizárólag újépítésű lakás vásárlására, illetve lakás
											építésére fordítható.
										</p>
									),
									"Lehet befektetési céllal is vásárolni lakást zöld hitelből?": (
										<p>
											Nem, elvárás, hogy az adott ingatlanban éljen az igénylő a vásárlást követő
											10 éven keresztül.
										</p>
									),
									"Ki lehet a tulajdonosa a zöld hitelből megvásárolt, felépített lakásnak?": (
										<p>Kizárólag az adósok, adóstársak lehetnek az ingatlan tulajdonosai.</p>
									),
									"Minden adósnak, adóstársnak tulajdonjogot kell szereznie a lakásban?": (
										<p>
											Nem, megfelelő az is, ha csak az egyik adós lesz az ingatlan kizárólagos
											tulajdonosa.
										</p>
									),
									"Szerezhet tulajdonjogot az igénylő gyermeke a lakásban?": (
										<p>
											Igen, az igénylő 25 évnél fiatalabb gyermekei is szerezhetnek tulajdonjogot
											a házban. Sőt nekik még adóstársan sem kell lenniük a hitelügyletben.
										</p>
									),
								}}
							/>
						</Col>
					</Row>
				</LandingContainer>
				<StructuredDataSchema schema={this.getSchema()} />
				{this.renderUsefulLinks()}
				<LandingBottom tags="zold-hitel,zold-lakashitel,lakashitel" />
			</>
		);
	}

	private renderTableOfContents(): ReactNode {
		return (
			<>
				<div className="h3 mb-3 mt-4">Tartalomjegyzék</div>
				<List itemStyle="link" className="text-secondary">
					<List.Item>
						<a
							className="text-underlined text-secondary"
							href="#green-loan-goal"
							onClick={() =>
								GoogleAnalytics4.sendEvent("BM_scrollTo", {
									buttonName: "toGreenLoanGoal",
								})
							}
						>
							A zöld hitel célja
						</a>
					</List.Item>
					<List.Item>
						<a
							className="text-underlined text-secondary"
							href="#green-loan-conditions"
							onClick={() =>
								GoogleAnalytics4.sendEvent("BM_scrollTo", {
									buttonName: "toGreenLoanConditions",
								})
							}
						>
							A zöld hitel igénylés feltételei
						</a>
					</List.Item>
					<List.Item>
						<a
							className="text-underlined text-secondary"
							href="#available-loan-bank"
							onClick={() =>
								GoogleAnalytics4.sendEvent("BM_scrollTo", {
									buttonName: "toAvailableLoanBank",
								})
							}
						>
							Hol érhető el jelenleg a zöld hitel?
						</a>
					</List.Item>
					<List.Item>
						<a
							className="text-underlined text-secondary"
							href="#available-loan-goals"
							onClick={() =>
								GoogleAnalytics4.sendEvent("BM_scrollTo", {
									buttonName: "toAvailableLoanGoals",
								})
							}
						>
							Milyen célra fordítható a hitelösszeg?
						</a>
					</List.Item>
					<List.Item>
						<a
							className="text-underlined text-secondary"
							href="#much-more-time"
							onClick={() =>
								GoogleAnalytics4.sendEvent("BM_scrollTo", {
									buttonName: "toMuchMoreTime",
								})
							}
						>
							Több idő áll rendelkezésre, mint más hiteleknél
						</a>
					</List.Item>
					<List.Item>
						<a
							className="text-underlined text-secondary"
							href="#why-is-it-worth-it"
							onClick={() =>
								GoogleAnalytics4.sendEvent("BM_scrollTo", {
									buttonName: "toWhyIsItWorthIt",
								})
							}
						>
							Miért éri meg kihasználnod?
						</a>
					</List.Item>
					<List.Item>
						<a
							className="text-underlined text-secondary"
							href="#interest-free-csok"
							onClick={() =>
								GoogleAnalytics4.sendEvent("BM_scrollTo", {
									buttonName: "toInterestFreeCsok",
								})
							}
						>
							Kamatmentes lesz a CSOK-hitel, ha zöld hitelként igényelnéd
						</a>
					</List.Item>
					<List.Item>
						<a
							className="text-underlined text-secondary"
							href="#plus-loan"
							onClick={() =>
								GoogleAnalytics4.sendEvent("BM_scrollTo", {
									buttonName: "toPlusLoan",
								})
							}
						>
							Nem elég a zöld CSOK-hitel?
						</a>
					</List.Item>
					<List.Item>
						<a
							className="text-underlined text-secondary"
							href="#gyik"
							onClick={() =>
								GoogleAnalytics4.sendEvent("BM_scrollTo", {
									buttonName: "toFaq",
								})
							}
						>
							Gyakran Ismételt Kérdések
						</a>
					</List.Item>
					<List.Item>
						<a
							className="text-underlined text-secondary"
							href="#links"
							onClick={() =>
								GoogleAnalytics4.sendEvent("BM_scrollTo", {
									buttonName: "toUsefulLinks",
								})
							}
						>
							Hasznos linkek zöld hitel témakörben
						</a>
					</List.Item>
				</List>
			</>
		);
	}

	private renderTable1(): ReactNode {
		return (
			<>
				<Table striped bordered className="three-column text-nowrap table-sm">
					<thead className="thead-dark text-center">
						<tr>
							<th colSpan={2}>
								A Zöld hitel előnye a jelenleg
								<br className="d-md-none" />
								elérhető CSOK-hitellel szemben
							</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<th scope="col" className="text-center">
								<InfoPopup tooltip="Október 4. előtti feltételek">
									CSOK-hitel <FontAwesomeIcon icon={fasCircleInfo} className="info" />
								</InfoPopup>
							</th>
							<th scope="col" className="text-center">
								Zöld hitel
							</th>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Hitelösszeg
							</th>
						</tr>
						<tr>
							<td className="text-center">15 000 000 Ft</td>
							<td className="text-center">15 000 000 Ft</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Kamat
							</th>
						</tr>
						<tr>
							<td className="text-center">3,00%</td>
							<td className="text-center">2,50%</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Kamatperiódus
							</th>
						</tr>
						<tr>
							<td className="text-center">Fix</td>
							<td className="text-center">Fix</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Futamidő
							</th>
						</tr>
						<tr>
							<td className="text-center">20 év</td>
							<td className="text-center">20 év</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Törlesztőrészlet
							</th>
						</tr>
						<tr>
							<td className="text-center">83 190 Ft</td>
							<td className="text-center">79 485 Ft</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Teljes visszafizetendő összeg
							</th>
						</tr>
						<tr>
							<td className="text-center">19 965 514 Ft</td>
							<td className="text-center">19 076 504 Ft</td>
						</tr>
						<tr>
							<td colSpan={2} className="text-danger text-center">
								<strong>889 ezer Ft megtakarítás</strong>
							</td>
						</tr>
					</tbody>
				</Table>
				<div className="text-right mb-3 small">
					Forrás: <strong>Bankmonitor</strong> - 2021.09.10.
				</div>
			</>
		);
	}

	private renderTable2(): ReactNode {
		return (
			<>
				<Table striped bordered className="three-column text-nowrap table-sm">
					<thead className="thead-dark text-center">
						<tr>
							<th colSpan={2}>
								A Zöld hitel előnye a<br className="d-md-none" /> jelenleg elérhető legkedvezőbb
								<br className="d-md-none" /> piaci lakáshitellel szemben
							</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<th scope="col" className="text-center">
								Piaci lakáshitel*
							</th>
							<th scope="col" className="text-center">
								Zöld hitel
							</th>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Hitelösszeg
							</th>
						</tr>
						<tr>
							<td className="text-center">30 000 000 Ft</td>
							<td className="text-center">30 000 000 Ft</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Kamat
							</th>
						</tr>
						<tr>
							<td className="text-center">4,52%</td>
							<td className="text-center">2,50%</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Kamatperiódus
							</th>
						</tr>
						<tr>
							<td className="text-center">Fix</td>
							<td className="text-center">Fix</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Futamidő
							</th>
						</tr>
						<tr>
							<td className="text-center">20 év</td>
							<td className="text-center">20 év</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Törlesztőrészlet
							</th>
						</tr>
						<tr>
							<td className="text-center">190 119 Ft</td>
							<td className="text-center">158 971 Ft</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Teljes visszafizetendő összeg
							</th>
						</tr>
						<tr>
							<td className="text-center">45 628 522 Ft</td>
							<td className="text-center">38 153 008 Ft</td>
						</tr>
						<tr>
							<td colSpan={2} className="text-danger text-center">
								<strong>7,4 millió Ft megtakarítás</strong>
							</td>
						</tr>
					</tbody>
				</Table>
				<div className="mb-3 text-muted">
					<i>
						* A Bankmonitor lakáshitel kalkulátora alapján a legolcsóbb lakáshitel kondíciói alapján (400
						ezer Ft havi jövedelem beállítása esetén).
					</i>
				</div>
				<div className="text-right mb-3 small">
					Forrás: <strong>Bankmonitor</strong> - 2021.09.10.
				</div>
			</>
		);
	}

	private renderTable3(): ReactNode {
		return (
			<>
				<Table striped bordered className="three-column text-nowrap table-sm">
					<thead className="thead-dark text-center">
						<tr>
							<th colSpan={2}>
								Az ingyenes CSOK-hitel előnye
								<br className="d-md-none" /> a jelenleg elérhető <br className="d-md-none" />
								CSOK-hitellel szemben
							</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<th scope="col" className="text-center">
								CSOK-hitel
								<br />
								(Október 4. előtt)
							</th>
							<th scope="col" className="text-center">
								CSOK-hitel
								<br />
								(Október 4. után)
							</th>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Hitelösszeg
							</th>
						</tr>
						<tr>
							<td className="text-center">15 000 000 Ft</td>
							<td className="text-center">15 000 000 Ft</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Kamat
							</th>
						</tr>
						<tr>
							<td className="text-center">3,00%</td>
							<td className="text-center">0,00%</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Kamatperiódus
							</th>
						</tr>
						<tr>
							<td className="text-center">Fix</td>
							<td className="text-center">Fix</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Futamidő
							</th>
						</tr>
						<tr>
							<td className="text-center">20 év</td>
							<td className="text-center">20 év</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Törlesztőrészlet
							</th>
						</tr>
						<tr>
							<td className="text-center">83 190 Ft</td>
							<td className="text-center">62 500 Ft</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Teljes visszafizetendő összeg
							</th>
						</tr>
						<tr>
							<td className="text-center">19 965 514 Ft</td>
							<td className="text-center">15 000 000 Ft</td>
						</tr>
						<tr>
							<td colSpan={2} className="text-danger text-center">
								<strong>4,96 millió Ft megtakarítás</strong>
							</td>
						</tr>
					</tbody>
				</Table>
				<div className="text-right mb-3 small">
					Forrás: <strong>Bankmonitor</strong> - 2021.09.10.
				</div>
			</>
		);
	}

	private renderTableCsokPlus(): ReactNode {
		return (
			<>
				<Table striped bordered className="three-column text-nowrap table-sm">
					<thead className="thead-dark text-center">
						<tr>
							<th colSpan={2}>
								A Zöld hitel és a CSOK támogatás
								<br className="d-md-none" /> összekombinálásának
								<br className="d-md-none" /> pénzügyi előnyei
							</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<th scope="col" className="text-center">
								CSOK-hitel
								<br />
								Október 4. előtt
							</th>
							<th scope="col" className="text-center">
								Zöld CSOK-hitel
								<br />
								Október 4. után
							</th>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Hitelösszeg
							</th>
						</tr>
						<tr>
							<td className="text-center">15 000 000 Ft</td>
							<td className="text-center">15 000 000 Ft</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Kamat
							</th>
						</tr>
						<tr>
							<td className="text-center">3%</td>
							<td className="text-center">0%</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Kamatperiódus
							</th>
						</tr>
						<tr>
							<td className="text-center">Fix</td>
							<td className="text-center">Fix</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Futamidő
							</th>
						</tr>
						<tr>
							<td className="text-center">20 év</td>
							<td className="text-center">20 év</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Törlesztőrészlet
							</th>
						</tr>
						<tr>
							<td className="text-center">83 190 Ft</td>
							<td className="text-center">62 500 Ft</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Teljes visszafizetendő összeg
							</th>
						</tr>
						<tr>
							<td className="text-center">19 965 514 Ft</td>
							<td className="text-center">15 000 000 Ft</td>
						</tr>

						<tr>
							<th scope="col" className="text-center">
								Piaci lakáshitel*
							</th>
							<th scope="col" className="text-center">
								Zöld hitel
							</th>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Hitelösszeg
							</th>
						</tr>
						<tr>
							<td className="text-center">30 000 000 Ft</td>
							<td className="text-center">30 000 000 Ft</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Kamat
							</th>
						</tr>
						<tr>
							<td className="text-center">4,52%</td>
							<td className="text-center">2,50%</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Kamatperiódus
							</th>
						</tr>
						<tr>
							<td className="text-center">Fix</td>
							<td className="text-center">Fix</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Futamidő
							</th>
						</tr>
						<tr>
							<td className="text-center">20 év</td>
							<td className="text-center">20 év</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Törlesztőrészlet
							</th>
						</tr>
						<tr>
							<td className="text-center">190 119 Ft</td>
							<td className="text-center">158 971 Ft</td>
						</tr>
						<tr>
							<th className="text-center" colSpan={2}>
								Teljes visszafizetendő összeg
							</th>
						</tr>
						<tr>
							<td className="text-center">45 628 522 Ft</td>
							<td className="text-center">38 153 008 Ft</td>
						</tr>
						<tr>
							<td colSpan={2} className="text-danger text-center">
								<strong>12,4 millió Ft megtakarítás</strong>
							</td>
						</tr>
					</tbody>
				</Table>
				<div className="text-right mb-3">
					*A Bankmonitor lakáshitel kalkulátora alapján a legolcsóbb lakáshitel kondíciói alapján (400 ezer Ft
					havi jövedelem beállítása esetén).
				</div>
			</>
		);
	}

	private renderUsefulLinks(): ReactNode {
		return (
			<LandingContainer>
				<div id="links" className="h3">
					Hasznos Linkek Zöld Hitel Témakörben
				</div>
				<ul>
					<li>
						<a
							href="https://www.mnb.hu/sajtoszoba/sajtokozlemenyek/2021-evi-sajtokozlemenyek/sikeres-zold-tokekovetelmeny-kedvezmeny-utan-sikerre-itelt-zold-lakashitelek"
							target="_blank"
							rel="noreferrer"
						>
							Sikeres zöld tőkekövetelmény-kedvezmény után sikerre ítélt zöld hitelek
						</a>
					</li>
					<li>
						<a
							href="https://www.mnb.hu/sajtoszoba/sajtokozlemenyek/2021-evi-sajtokozlemenyek/zold-monetaris-politikai-eszkoztar-strategiat-hirdetett-a-magyar-nemzeti-bank"
							target="_blank"
							rel="noreferrer"
						>
							Zöld monetáris politikai eszköztár-stratégiát hirdetett a Magyar Nemzeti Bank
						</a>
					</li>
					<li>
						<a href="https://net.jogtar.hu/jogszabaly?docid=a0800176.kor" target="_blank" rel="noreferrer">
							zöld hitellel kapcsolatos hatályos jogszabályok és rendeletek
						</a>
					</li>
					<li>
						<a
							href="https://www.mnb.hu/letoltes/zold-eszkoztar-strategia-publikacio-2021-hun-0706-2.pdf"
							target="_blank"
							rel="noreferrer"
						>
							Fenntarthatóság és jegybanki politika - zöld szempontok az MNB monetáris politikai
							eszköztárában
						</a>
					</li>
					<li>
						<a
							href="https://kormany.hu/hirek/egyszerubb-feltetelek-jonnek-az-otthonteremtesi-tamogatasoknal"
							target="_blank"
							rel="noreferrer"
						>
							Egyszerűbb feltételek jönnek az otthonteremtési támogatásoknál
						</a>
					</li>
					<li>
						<a
							href="https://www.mnb.hu/sajtoszoba/sajtokozlemenyek/2021-evi-sajtokozlemenyek/sikeres-zold-tokekovetelmeny-kedvezmeny-utan-sikerre-itelt-zold-lakashitelek"
							target="_blank"
							rel="noreferrer"
						>
							Sajtóközlemény: Sikeres zöld tőkekövetelmény-kedvezmény után sikerre ítélt zöld hitelek
						</a>
					</li>
					<li>
						<a
							href="https://www.mnb.hu/letoltes/nhp-zop-termektajekoztato-20210916.pdf"
							target="_blank"
							rel="noreferrer"
						>
							NHP Zöld Otthon Program - MNB terméktájékoztató
						</a>
					</li>
					<li>
						<a href="https://www.mnb.hu/zold-otthon-program" target="_blank" rel="noreferrer">
							Zöld Otthon Programmal kapcsolatos információk az MNB honlapján
						</a>
					</li>
				</ul>
			</LandingContainer>
		);
	}

	private renderNotificationBlock(): ReactNode {
		return (
			<LandingContainer bgClassName="bg-trianglify-green bg-border-y" className="text-center" bgColor="#107353">
				<div className="h2 mb-3 text-white">
					Próbáld ki új Zöld Hitel Kalkulátorunkat és tudd meg, jogosult vagy-e az új hitelre!
				</div>
				<div className="text-center">
					<Button
						onClick={() => this.scrollToSubscribe()}
						variant="light"
						icon={<FontAwesomeIcon icon={fasCalculator} />}
						size="lg"
					>
						Kalkulálok
					</Button>
				</div>
			</LandingContainer>
		);
	}

	private scrollToSubscribe(): void {
		scrollToElement("calculator");
	}

	private getSchema(): FAQSchema {
		return {
			"@context": "https://schema.org",
			"@type": "FAQPage",
			mainEntity: [
				{
					"@type": "Question",
					name: "Emelkedhetnek a lakásárak októbertől az új támogatott hitel bevezetése miatt?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Az új támogatások természetesen befolyásolhatják a lakásárakat is. Ugyanakkor a lakáspiacra jelenleg számos tényező hat ezek együttes hatását pedig előre nehéz megbecsülni.",
					},
				},
				{
					"@type": "Question",
					name: "Elfogyhat a 200 milliárd forintos keretösszeg?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Nem tudhatjuk előre, hogy meddig lesz elég az MNB által biztosított keretösszeg. (2 857 igénylő tudná felvenni a kölcsönt, ha mindenki kihasználná a maximális 70 millió Ft hitelösszeget.)",
					},
				},
				{
					"@type": "Question",
					name: "Mennyire szigorú a „BB” energetikai besorolás?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "A kategória elnyeréséhez elvárás, hogy a ház energiafelhasználásának <strong>legalább 25 százaléka megújuló forrásból származzon.</strong> (2022. júliusától már csak ennek megfelelő lakás készülhet el.)",
					},
				},
				{
					"@type": "Question",
					name: "Szükséges gyermek a zöld hitelhez?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Nem szükséges",
					},
				},
				{
					"@type": "Question",
					name: "Szükséges gyermek a kamatmentes Zöld CSOK-hitelhez?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Igen, legalább két meglévő, vagy vállalt gyermek szükséges a kamatmentesség elnyeréséhez.",
					},
				},
				{
					"@type": "Question",
					name: "Csak házasok kaphatják meg a zöld hitelt?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Nem, egyedülállóként is fel lehet venni a zöld hitelt.",
					},
				},
				{
					"@type": "Question",
					name: "Csak házasok kaphatják meg a kamatmentes Zöld CSOK-hitelt?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Nem egyedülállók is megkaphatják az ingyenes zöld hitelt. Egyedülállóként viszont elvárás, hogy legyen legalább két meglévő gyermek.",
					},
				},
				{
					"@type": "Question",
					name: "Fel lehet venni a Babaváró hitelt a zöld hitellel együtt?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Igen, erre vonatkozóan nincsen tiltás.",
					},
				},
				{
					"@type": "Question",
					name: "Fel lehet venni a CSOK támogatást a zöld hitel mellé?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "	Igen, sőt a kamatmentes zöld CSOK-hitelnek igénylési feltétele is, hogy a CSOK támogatást legalább két gyermekre igénybe vegyék.",
					},
				},
				{
					"@type": "Question",
					name: "Fel lehet venni a falus CSOK-ot a zöld hitel mellé?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Nem, mert eltérő célokra használható fel a két támogatási forma.",
					},
				},
				{
					"@type": "Question",
					name: "Fel lehet venni a felújítási támogatást a zöld hitel mellé?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Igen, újépítésű lakás „felújítására” is fordítható az otthonfelújítási támogatás.",
					},
				},
				{
					"@type": "Question",
					name: "Fel lehet venni a zöld hitelt meglévő lakás felújítására, korszerűsítésére?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "	Nem, a zöld hitel kizárólag újépítésű lakás vásárlására, illetve lakás építésére fordítható.",
					},
				},
				{
					"@type": "Question",
					name: "Lehet befektetési céllal is vásárolni lakást zöld hitelből?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Nem, elvárás, hogy az adott ingatlanban éljen az igénylő a vásárlást követő 10 éven keresztül.",
					},
				},
				{
					"@type": "Question",
					name: "Ki lehet a tulajdonosa a zöld hitelből megvásárolt, felépített lakásnak?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Kizárólag az adósok, adóstársak lehetnek az ingatlan tulajdonosai.",
					},
				},
				{
					"@type": "Question",
					name: "Minden adósnak, adóstársnak tulajdonjogot kell szereznie a lakásban?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Nem, megfelelő az is, ha csak az egyik adós lesz az ingatlan kizárólagos tulajdonosa.",
					},
				},
				{
					"@type": "Question",
					name: "Szerezhet tulajdonjogot az igénylő gyermeke a lakásban?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Igen, az igénylő 25 évnél fiatalabb gyermekei is szerezhetnek tulajdonjogot a házban. Sőt nekik még adóstársan sem kell lenniük a hitelügyletben.",
					},
				},
			],
		};
	}
}
